import { Box } from '@radix-ui/themes';
import React from 'react';
import {
    Label,
    Pie,
    PieProps,
    PieSectorShapeProps,
    PieChart as RePieChart,
    Sector,
} from 'recharts';
import { ChartItem, ValueFormatter } from '.';

import styles from './Charts.module.css';

interface PieChartProps extends PieProps {
    data: ChartItem[];
    centerLabel?: React.ReactNode;
    hoveredName?: string;
    onHover?: (name: string | undefined) => void;
    valueFormatter?: ValueFormatter;
}

interface PieShapeProps extends PieSectorShapeProps {
    valueFormatter?: ValueFormatter;
}

const InactivePieShape = (props: PieShapeProps) => {
    return <Sector {...props} />;
};

const ActivePieShape = (props: PieShapeProps) => {
    const {
        value,
        percent,
        cx: centerX = 0, // Center X of the Pie
        cy: centerY = 0, // Center Y of the Pie
        midAngle = 0, // Angle from the horizontal center of the pie to the center of the section
        outerRadius = 0,
        payload,
        fill,
        valueFormatter,
    } = props;

    const gap = 5;
    const thickness = 3;
    const activeSectorSize = gap + thickness;

    /**
     * Recharts measures angles clockwise
     * while standard Math measures counter-clockwise.
     * We need to negate the @RADIAN to stay in sync.
     */
    const RADIAN = -Math.PI / 180;
    const cos = Math.cos(RADIAN * midAngle); // Get the horizontal ratio of the midAngle (-1 to 1)
    const sin = Math.sin(RADIAN * midAngle); // Get the vertical ratio of the midAngle (-1 to 1)
    const horizontalDir = cos >= 0 ? 1 : -1;
    const isRightHalf = horizontalDir === 1;
    const verticalDir = sin >= 0 ? 1 : -1;
    const isTopHalf = verticalDir === 1;

    // 0. Starting Point
    const startX = centerX + (outerRadius + activeSectorSize) * cos; // X component of the point activeSectorSize from outerRadius in the midAngle direction
    const startY = centerY + (outerRadius + activeSectorSize) * sin; // Y component of the point activeSectorSize from outerRadius in the midAngle direction

    // 1. SEGMENT 1
    const seg1Length = 12; // Length of the first segment
    const point1X = startX + seg1Length * cos; // X component of the point seg1Length from startX in the midAngle direction
    const point1Y = startY + seg1Length * sin; // Y component of the point seg1Length from startX in the midAngle direction

    // 2. SEGMENT 2
    const seg2Length = 50; // Length of the second segment
    const point2X = centerX + (outerRadius + seg2Length) * horizontalDir; // X componnent of the 2nd point, we don't use cos here to keep the distance fixed for a "column-like" result
    const point2Y = point1Y; // Y component of the 2nd point, we keep it equal to the Y component of point1 to make the segment horizontal (we also could use "H${point2X}" in the path parameters instead)

    // 3. SEGMENT 3
    const seg3Length = 14; // Length of the third segment
    const cos45 = Math.cos(Math.PI / 4); // = Math.sin(Math.PI /4) = 0.707
    const point3X = point2X + seg3Length * cos45 * horizontalDir; // X component of the 3rd point seg3length from the 2nd one at 45° angle
    const point3Y = point2Y + seg3Length * cos45 * -verticalDir; // Y component of the 3rd point seg3length from the 2nd one at 45° angle, bending toward the vertical center

    const textAnchor = isRightHalf ? 'start' : 'end';
    const percentYOffset = isTopHalf ? 16 : -8;
    const valueYOffset = !isTopHalf ? 16 : -8;

    const displayValue = valueFormatter ? valueFormatter(value) : value;

    return (
        <g>
            <text
                x={centerX}
                y={centerY}
                fill={fill}
                fontSize={'16px'}
                fontWeight={'bold'}
                textAnchor={'middle'}
                alignmentBaseline={'central'}
            >
                {payload.name}
            </text>

            <InactivePieShape {...props} className={styles.active} />

            <Sector
                {...props}
                innerRadius={outerRadius + gap}
                outerRadius={outerRadius + activeSectorSize}
            />

            <path
                d={`M${startX},${startY} 
                    L${point1X},${point1Y} 
                    L${point2X},${point2Y} 
                    L${point3X},${point3Y}`}
                stroke={fill}
                fill={'none'}
                strokeWidth={2}
                strokeLinejoin={'round'}
                strokeLinecap={'round'}
            />

            <circle
                cx={point3X}
                cy={point3Y}
                r={2.5}
                fill={fill}
                stroke={'none'}
            />

            <text
                x={point3X + (cos >= 0 ? 8 : -8)}
                y={point3Y}
                dy={percentYOffset}
                textAnchor={textAnchor}
                fill={'var(--gray-10)'}
                style={{ fontSize: '12px' }}
            >
                {`(${(percent! * 100).toFixed(1)}%)`}
            </text>

            <text
                x={point3X + (cos >= 0 ? 8 : -8)}
                y={point3Y}
                dy={valueYOffset}
                textAnchor={textAnchor}
                fill={'var(--gray-12)'}
                style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
                {displayValue}
            </text>
        </g>
    );
};

const PieShape = (props: PieShapeProps) => {
    if (props.isActive) return <ActivePieShape {...props} />;
    return <InactivePieShape {...props} />;
};

export const PieChart = ({
    centerLabel,
    hoveredName,
    onHover,
    valueFormatter,
    ...props
}: PieChartProps) => {
    return (
        <Box maxHeight={'300px'} maxWidth={'500px'} minWidth={'500px'} asChild>
            <RePieChart width={'100%'} height={'100%'} tabIndex={-1} responsive>
                <Pie
                    minAngle={3}
                    {...props}
                    shape={(props) => (
                        <PieShape
                            {...props}
                            isActive={
                                props.name === hoveredName || props.isActive
                            }
                            valueFormatter={valueFormatter}
                        />
                    )}
                    onMouseEnter={(item) => onHover?.(item.name)}
                    onMouseLeave={() => onHover?.(undefined)}
                    cx={'50%'}
                    cy={'50%'}
                    innerRadius={'55%'}
                    outerRadius={'80%'}
                    paddingAngle={4}
                    cornerRadius={3}
                    stroke={'none'}
                    rootTabIndex={-1}
                    tabIndex={-1}
                >
                    {centerLabel && (
                        <Label position={'center'} fill={props.fill}>
                            {`${centerLabel}`}
                        </Label>
                    )}
                </Pie>
            </RePieChart>
        </Box>
    );
};
